"use client";

import { useEffect, useMemo, useRef, useState } from "react";

export type FontItem = {
  id: string;
  family: string;
  role?: "heading" | "text" | "other";
  variants?: {
    regular?: boolean;
    bold?: boolean;
    italic?: boolean;
    boldItalic?: boolean;
  };
};

type Props = {
  fonts: FontItem[];
  value: string; // current font family
  onSelect: (family: string) => void;
  disabled?: boolean;
};

export default function FontPicker({ fonts, value, onSelect, disabled }: Props) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const wrapRef = useRef<HTMLDivElement | null>(null);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return fonts;
    return fonts.filter((f) => f.family.toLowerCase().includes(qq));
  }, [fonts, q]);
  const grouped = useMemo(() => {
    const byRole = new Map<string, FontItem[]>();
    for (const font of filtered) {
      const role = font.role || "other";
      byRole.set(role, [...(byRole.get(role) || []), font]);
    }
    return ["heading", "text", "other"]
      .map((role) => ({ role, fonts: byRole.get(role) || [] }))
      .filter((group) => group.fonts.length > 0);
  }, [filtered]);

  // English comment: Close on outside click and Escape
  useEffect(() => {
    if (!open) return;

    const onDown = (e: MouseEvent) => {
      const el = wrapRef.current;
      if (!el) return;
      if (!el.contains(e.target as Node)) setOpen(false);
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };

    window.addEventListener("mousedown", onDown, true);
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("mousedown", onDown, true);
      window.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  const current = useMemo(() => {
    return fonts.find((f) => f.family === value) || null;
  }, [fonts, value]);

  const sample = (family: string) => family;
  const roleLabel = (role: string) => {
    if (role === "heading") return "Heading";
    if (role === "text") return "Text";
    return "Other";
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        className="w-full rounded-lg border border-black/10 bg-white px-3 py-2 text-left text-sm hover:bg-gray-50 disabled:opacity-50"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Choose font"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="truncate font-medium" style={{ fontFamily: value }}>
              {current?.family || value || "Font"}
            </div>
            <div className="truncate text-xs text-gray-500" style={{ fontFamily: value }}>
              {sample(current?.family || value || "Font")}
            </div>
          </div>
          <div className="text-xs text-gray-500">▾</div>
        </div>
      </button>

      {open ? (
        <div className="absolute z-50 mt-2 w-full max-w-[92vw] overflow-hidden rounded-xl border border-black/10 bg-white shadow-xl">
          <div className="border-b border-black/10 p-2">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full rounded-lg border border-black/10 px-3 py-2 text-sm outline-none focus:border-black/20"
              placeholder="Search font…"
              autoFocus
            />
          </div>

          <div className="max-h-[320px] overflow-auto p-2">
            {grouped.map((group) => (
              <div key={group.role} className="py-1">
                <div className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                  {roleLabel(group.role)}
                </div>

                {group.fonts.map((f) => {
                  const active = f.family === value;
                  return (
                    <button
                      key={f.id}
                      type="button"
                      className={`w-full rounded-lg px-3 py-2 text-left transition-colors ${
                        active ? "bg-[#272727] text-white" : "hover:bg-gray-50"
                      }`}
                      onClick={() => {
                        onSelect(f.family);
                        setOpen(false);
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`h-9 w-9 shrink-0 rounded-lg border ${
                            active ? "border-white/20 bg-white/10" : "border-black/10 bg-white"
                          } flex items-center justify-center`}
                          style={{ fontFamily: f.family }}
                        >
                          Aa
                        </div>

                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium" style={{ fontFamily: f.family }}>
                            {f.family}
                          </div>
                          <div
                            className={`truncate text-xs ${active ? "text-white/80" : "text-gray-500"}`}
                            style={{ fontFamily: f.family }}
                          >
                            {sample(f.family)}
                          </div>
                        </div>

                        <div className={`text-[11px] ${active ? "text-white/80" : "text-gray-400"}`}>
                          {f.variants?.italic ? "I" : ""}
                          {f.variants?.bold ? "B" : ""}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            ))}

            {filtered.length === 0 ? (
              <div className="px-3 py-6 text-sm text-gray-500">No fonts found.</div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
